/**
 * LogPrepModal Component
 *
 * Modal dialog for logging a recipe preparation.
 * Features:
 * - Date input (default today)
 * - Servings input (optional, default from recipe)
 * - Notes textarea (optional)
 * - Submit via POST to /food/recipes/:id/preps
 */

import { useState, useCallback, useEffect, useRef } from 'react';

interface LogPrepModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when prep is saved successfully */
  onSave: () => void;
  /** Recipe ID to log prep for */
  recipeId: number;
  /** Default servings from recipe */
  defaultServings?: number | null;
}

function XIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CalendarIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

export function LogPrepModal({
  isOpen,
  onClose,
  onSave,
  recipeId,
  defaultServings,
}: LogPrepModalProps) {
  const [preparedAt, setPreparedAt] = useState(getTodayString());
  const [servings, setServings] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPreparedAt(getTodayString());
      setServings(defaultServings?.toString() || '');
      setNotes('');
      setError(null);
    }
  }, [isOpen, defaultServings]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement?.focus();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isSaving) {
        onClose();
      }
    },
    [onClose, isSaving]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!preparedAt) {
        setError('Please select a date');
        return;
      }

      setIsSaving(true);
      setError(null);

      try {
        // Convert date to ISO format with time
        const preparedAtISO = new Date(preparedAt + 'T12:00:00').toISOString();

        const response = await fetch(`/food/recipes/${recipeId}/preps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          },
          body: JSON.stringify({
            prep: {
              prepared_at: preparedAtISO,
              servings_made: servings ? parseInt(servings, 10) : null,
              notes: notes.trim() || null,
            },
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to log preparation');
        }

        onSave();
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to log preparation');
      } finally {
        setIsSaving(false);
      }
    },
    [preparedAt, servings, notes, recipeId, onSave, onClose]
  );

  const canSubmit = preparedAt !== '' && !isSaving;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      data-testid="log-prep-modal-backdrop"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="log-prep-modal-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="log-prep-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 id="log-prep-modal-title" className="text-lg font-semibold text-gray-900">Log Preparation</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            aria-label="Close modal"
          >
            <XIcon />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Date */}
            <div>
              <label
                htmlFor="prep-date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <CalendarIcon className="w-4 h-4" />
                </div>
                <input
                  id="prep-date"
                  type="date"
                  value={preparedAt}
                  onChange={(e) => setPreparedAt(e.target.value)}
                  disabled={isSaving}
                  max={getTodayString()}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  data-testid="prep-date-input"
                />
              </div>
            </div>

            {/* Servings */}
            <div>
              <label
                htmlFor="servings"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Servings Made <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="servings"
                type="number"
                min="1"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                disabled={isSaving}
                placeholder={defaultServings ? `Default: ${defaultServings}` : 'Number of servings'}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                data-testid="servings-input"
              />
            </div>

            {/* Notes */}
            <div>
              <label
                htmlFor="prep-notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                id="prep-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSaving}
                rows={3}
                placeholder="Any notes about this preparation..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
                data-testid="prep-notes-textarea"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="log-prep-button"
            >
              {isSaving ? 'Saving...' : 'Log Preparation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
